import React, { useMemo, useState } from 'react';
import { Tooltip, SelectMenu, Button, Banner } from '@scale/scaleui';
import { ExtendedGreenhouseJobBoardJob, JobPostQuestion } from '@scale/shared-types';
import WhatToExpectCards from './WhatToExpectCards';
import JobHighlightCards from './JobHighlightCards';
import redirect from '@scale/frontend-lib/isomorphic-redirect';
import { OUTLIER_FRONTEND_URL } from 'react-scale/lib/constants';
import { OUTLIER_NEXT_URL } from 'react-scale/remotasks.com/components/Expert/constants';
import logger from '@scale/frontend-lib/logging';

function addListStyling(htmlString: string): string {
  return htmlString.replace(/<ul>/g, '<ul class="list-disc list-inside">'); // gets around default tailwindcss styling
}

interface PreviewJobPostDetailsProps {
  job: ExtendedGreenhouseJobBoardJob;
  viewOnly: boolean;
  applicationFields: Record<string, string>;
  setApplicationFields: (fields: Record<string, string>) => void;
  signupFlowId?: string;
}

interface LiveJobPostDetailsProps extends PreviewJobPostDetailsProps {
  viewOnly: false;
  resume: File | undefined;
  setResume: (file: File | undefined) => void;
  submitApplicationButton: JSX.Element;
}

type JobPostDetailsProps = PreviewJobPostDetailsProps | LiveJobPostDetailsProps;

const directToSignupV2 = (jobPostId: string, useNewOnboarding: boolean, signupFlowId?: string) => {
  if (useNewOnboarding) {
    // Redirect to the new outlier-next login page with job_post_id
    const url = new URL(`${OUTLIER_NEXT_URL}/login`);
    url.searchParams.append('job_post_id', jobPostId);
    if (signupFlowId) {
      url.searchParams.append('signup_flow', signupFlowId);
    }
    redirect({}, url.toString(), { forwardQueryParams: true, absolute: true });
  } else {
    // Redirect to the old apply page (uses 'id' param)
    const url = new URL(`${OUTLIER_FRONTEND_URL}/apply`);
    url.searchParams.append('id', jobPostId);
    redirect({}, url.toString(), { forwardQueryParams: true, absolute: true });
  }
};

const ApplyNowButton: React.FC<{
  jobPostId: string;
  viewOnly: boolean;
  useNewOnboarding: boolean;
  signupFlowId?: string;
}> = ({ jobPostId, viewOnly, useNewOnboarding, signupFlowId }) => {
  return (
    <div
      className="font-semibold md:text-base max-md:text-sm border rounded-full bg-neutral-800 cursor-pointer inline-block"
      onClick={() => {
        if (!viewOnly) {
          directToSignupV2(jobPostId, useNewOnboarding, signupFlowId);
        }
      }}
    >
      <div className="w-max md:py-4 md:px-10 max-md:px-4 max-md:py-2 text-neutral-100">
        APPLY <span className="max-md:hidden">NOW</span>
      </div>
    </div>
  );
};

const ApplyNowButtonForQuestions: React.FC<{
  hasNotFilledOutQuestions: boolean;
  viewOnly: boolean;
  areAllV2AnswersCorrect: boolean;
  jobPostId: string;
  showRedirectBanner: boolean;
  setShowRedirectBanner: (show: boolean) => void;
  useNewOnboarding: boolean;
  signupFlowId?: string;
}> = ({
  hasNotFilledOutQuestions,
  viewOnly,
  areAllV2AnswersCorrect,
  jobPostId,
  showRedirectBanner,
  setShowRedirectBanner,
  useNewOnboarding,
  signupFlowId,
}) => {
  return (
    <div className="flex flex-col w-full relative">
      <Tooltip
        text={
          hasNotFilledOutQuestions
            ? 'Please fill out all required fields'
            : viewOnly && areAllV2AnswersCorrect
            ? 'Job questions are correct, please go to live job link to apply'
            : ''
        }
        target={
          <Button
            className="w-full"
            fullWidth
            variant="primary"
            size="lg"
            disabled={hasNotFilledOutQuestions || (viewOnly && areAllV2AnswersCorrect)}
            onClick={() => {
              if (areAllV2AnswersCorrect) {
                directToSignupV2(jobPostId, useNewOnboarding, signupFlowId);
              } else {
                setShowRedirectBanner(true);
                if (!viewOnly) {
                  logger.info(`Redirected user to Opportunities page for ${jobPostId}`);
                }
              }
            }}
          >
            Apply Now
          </Button>
        }
      />
      {showRedirectBanner && (
        <div className="w-full">
          <Banner
            className="mt-4"
            background="neutral"
            onClose={() => setShowRedirectBanner(false)}
            subtitle="Please check out other opportunities that match your expertise"
            title="Opportunity Requires Highly Specific Expertise"
            variant="info"
            cta={{
              label: 'View Opportunities',
              onClick: () => {
                redirect({}, `/en/expert/opportunities`, {
                  forwardQueryParams: true,
                });
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

const JobPostDetails: React.FC<JobPostDetailsProps> = (props: JobPostDetailsProps) => {
  const { job, viewOnly, signupFlowId, applicationFields, setApplicationFields } = props;
  if (!viewOnly) {
    const { setResume, submitApplicationButton } = props as LiveJobPostDetailsProps;
    if (!setResume || !submitApplicationButton) {
      throw new Error(
        'Resume and submit button are required when viewOnly is false for JobPostDetails',
      );
    }
  }

  const isSignupV2 = !!signupFlowId;
  const jobQuestionsV2 = useMemo(() => {
    if (isSignupV2) {
      return (job.questions || []) as JobPostQuestion[];
    }
    return [] as JobPostQuestion[];
  }, [isSignupV2, job.questions]);

  const areAllV2AnswersCorrect = useMemo(() => {
    if (!isSignupV2 || !jobQuestionsV2.length) {
      return true;
    }
    const checkAnswer = (question: JobPostQuestion) => {
      const userAnsweredYes = applicationFields[question.text]?.toLowerCase() === 'yes';
      return userAnsweredYes === question.answer;
    };
    if (!job.requiresAllQuestionsPassed) {
      return jobQuestionsV2.some(checkAnswer);
    }
    return jobQuestionsV2.every(checkAnswer);
  }, [isSignupV2, jobQuestionsV2, job.requiresAllQuestionsPassed, applicationFields]);

  const handleFieldChange = (questionText: string, value: string) => {
    const newFields: Record<string, string> = {};
    jobQuestionsV2.forEach(q => {
      if (q.text === questionText) {
        newFields[q.text] = value;
      } else if (applicationFields[q.text]) {
        newFields[q.text] = applicationFields[q.text];
      }
    });

    setApplicationFields(newFields);
  };
  const [showRedirectBanner, setShowRedirectBanner] = useState(false);
  const useNewOnboarding = job.useNewOnboarding ?? false;

  const columnWidth = 'max-md:max-w-full md:max-w-4xl lg:max-w-6xl';
  const applicationContentWidth = 'max-md:max-w-full md:max-w-2xl';
  return (
    <>
      <div className="flex md:px-4 lg:px-16">
        <div className={`flex flex-col ${columnWidth}`}>
          {/* Title section */}
          <div
            className={`flex items-start justify-between mb-12 max-md:max-w-full md:max-w-[1063px]`}
          >
            <div className="flex flex-col">
              <h1 className="text-3xl font-bold text-neutral-1000 mr-4">{job.title}</h1>
              <div className="text-sm font-small text-neutral-700 py-4">{job.location.name}</div>
            </div>
            {isSignupV2 && !jobQuestionsV2.length && (
              <div className="flex">
                <ApplyNowButton
                  jobPostId={job.id.toString()}
                  viewOnly={viewOnly}
                  useNewOnboarding={!!useNewOnboarding}
                  signupFlowId={signupFlowId}
                />
              </div>
            )}
          </div>

          <div className="flex">
            <div
              className={`text-sm font-small`}
              dangerouslySetInnerHTML={{ __html: addListStyling(job.content || '') }}
            />
            <div className="mt-14 max-lg:hidden">
              <JobHighlightCards />
            </div>
          </div>

          <div className="mt-8 lg:hidden w-full">
            <JobHighlightCards />
          </div>
          <div className="">
            <WhatToExpectCards />
          </div>
          <div className={`flex flex-col ${applicationContentWidth}`}>
            {isSignupV2 && jobQuestionsV2.length > 0 && (
              <>
                <div className="font-bold mt-10 mb-3">Application information</div>
                {jobQuestionsV2.map(question => (
                  <SelectMenu
                    key={question.text}
                    label={question.text}
                    options={[
                      { id: 'yes', name: 'Yes' },
                      { id: 'no', name: 'No' },
                    ]}
                    // @ts-ignore: allow field to be undefined if user hasn't answered
                    selectedOptionId={applicationFields[question.text] || undefined}
                    setSelected={optionId => handleFieldChange(question.text, optionId as string)}
                    className="my-2"
                    required={true}
                    buttonClassName="h-12"
                  />
                ))}
              </>
            )}
            {
              <div className="mt-10 mb-16 flex">
                {!jobQuestionsV2.length ? (
                  <ApplyNowButton
                    jobPostId={job.id.toString()}
                    viewOnly={viewOnly}
                    useNewOnboarding={useNewOnboarding}
                    signupFlowId={signupFlowId}
                  />
                ) : (
                  <ApplyNowButtonForQuestions
                    hasNotFilledOutQuestions={
                      Object.keys(applicationFields).length !== jobQuestionsV2.length ||
                      !jobQuestionsV2.every(q => applicationFields[q.text] !== undefined)
                    }
                    viewOnly={viewOnly}
                    areAllV2AnswersCorrect={areAllV2AnswersCorrect}
                    jobPostId={job.id.toString()}
                    showRedirectBanner={showRedirectBanner}
                    setShowRedirectBanner={setShowRedirectBanner}
                    useNewOnboarding={useNewOnboarding}
                    signupFlowId={signupFlowId}
                  />
                )}
              </div>
            }
          </div>
        </div>
      </div>
    </>
  );
};

export default JobPostDetails;
